import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WarrantyRegister } from "@/components/WarrantyRegister";

// Central CRM queue: warranty registrations to complete when a job is delivered.
export const WarrantyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [missing, setMissing] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [lockProduct, setLockProduct] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/warranty/pending")
      .then(({ data }) => {
        setTasks(data.tasks || []);
        setMissing(data.missing_service || []);
      })
      .catch(() => {
        setTasks([]);
        setMissing([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (t) => {
    setPrefill({
      job_id: t.job_id,
      studio_id: t.studio_id,
      studio_name: t.studio_name,
      owner_name: t.customer_name || "",
      owner_phone: t.customer_phone || "",
      crm_ref: t.job_id,
    });
    setLockProduct(t.product_type || null);
    setOpen(true);
  };

  return (
    <Card className="border-brand-line shadow-none" data-testid="warranty-tasks-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Pending warranty registrations</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Complete these when a studio delivers a warrantable Veloskin PPF job.
          </p>
        </div>
        <Badge className="border-0 bg-brand-tint text-brand" data-testid="warranty-tasks-count">
          {tasks.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        {missing.length > 0 && (
          <div
            className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4"
            data-testid="warranty-missing-service"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">
                  {missing.length} delivered job{missing.length > 1 ? "s" : ""} missing
                  service &amp; products
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  These jobs were delivered without any service or product logged, so the warranty
                  cannot be registered yet. Ask the studio to open each job and add the service and
                  products used — it will then appear in the pending list above automatically.
                </p>
                <div className="mt-3 space-y-2">
                  {missing.map((m) => (
                    <div
                      key={m.job_id}
                      data-testid={`warranty-missing-${m.job_id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white p-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-brand-ink">
                          {m.customer_name || "Customer"}
                          {m.vehicle && <span className="text-slate-500"> · {m.vehicle}</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {m.studio_name} · {m.status}
                          {m.delivered_on && ` · delivered ${fmtDate(m.delivered_on)}`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-400 text-amber-800 hover:bg-amber-100"
                        data-testid={`open-missing-job-${m.job_id}`}
                        onClick={() => navigate(`/jobs/${m.job_id}`)}
                      >
                        Add service &amp; products
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {tasks.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="warranty-tasks-empty">
            Nothing pending — every delivered warrantable job is registered.
          </p>
        )}
        {tasks.map((t) => (
          <div
            key={t.unit_id || t.job_id}
            data-testid={`warranty-task-${t.unit_id || t.job_id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-line p-4"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2 font-medium text-brand-ink">
                <span>{t.customer_name}</span>
                {t.vehicle && <span className="text-slate-500"> · {t.vehicle}</span>}
                {t.product_label && (
                  <Badge
                    data-testid={`warranty-task-product-${t.unit_id || t.job_id}`}
                    className={`border-0 ${
                      t.product_type === "Velotint Sunfilm"
                        ? "bg-amber-100 text-amber-900"
                        : "bg-brand-tint text-brand"
                    }`}
                  >
                    {t.product_label}
                  </Badge>
                )}
                <Badge className="border-0 bg-rose-50 text-rose-700" data-testid={`warranty-task-status-${t.unit_id || t.job_id}`}>
                  Pending
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                {t.studio_name} · delivered {fmtDate(t.delivered_on)} · {t.days_pending} day(s)
                pending · {t.eligible.map((e) => `${e.service} (${e.months}m)`).join(", ")}
              </p>
              {t.assigned_staff && (
                <p className="text-xs text-slate-400">Assigned to {t.assigned_staff}</p>
              )}
            </div>
            <Button
              size="sm"
              className="min-h-[44px]"
              data-testid={`register-warranty-task-${t.unit_id || t.job_id}`}
              onClick={() => openForm(t)}
            >
              <ShieldCheck className="mr-2 h-4 w-4" /> Register {t.product_label || "warranty"}
            </Button>
          </div>
        ))}
      </CardContent>

      <WarrantyRegister
        open={open}
        onOpenChange={setOpen}
        prefill={prefill}
        lockProduct={lockProduct}
        onDone={load}
      />
    </Card>
  );
};
